import { z } from "zod";

import { CUSTOMER_SEGMENTS } from "./analytics.js";
import { isNotificationActionUrl } from "./notifications.js";

// ---- storefront banners ----------------------------------------------------------------------
// A banner is a scheduled campaign of slides on an environment's storefront (docs/storefront.md).
// Tenant editors write them in the dashboard (Storefront → Banners); the BFF serves the ones live
// for the visiting shopper's spend band.

/**
 * Who a banner is shown to: a signed-out visitor, or a signed-in shopper's customer segment
 * (trailing 30-day spend, `customerSegment.ts` in core), lowest first. A banner that names none
 * is shown to everyone.
 */
export const BANNER_SPEND_BANDS = ["signed_out", ...CUSTOMER_SEGMENTS] as const;
export type BannerSpendBand = (typeof BANNER_SPEND_BANDS)[number];
export const bannerSpendBandSchema = z.enum(BANNER_SPEND_BANDS);

/** The most enabled banners one environment may have live at the same moment. */
export const BANNER_MAX_LIVE = 5;
/** The most slides one banner carries. */
export const BANNER_MAX_SLIDES = 10;
export const BANNER_NAME_MAX = 100;
export const BANNER_ALT_TEXT_MAX = 200;
export const BANNER_ACTION_LABEL_MAX = 40;
const BANNER_IMAGE_URL_MAX = 2000;
const BANNER_ACTION_URL_MAX = 500;

/** A slide's call to action: a button label and where it goes. */
export const bannerActionSchema = z.object({
  label: z.string().trim().min(1).max(BANNER_ACTION_LABEL_MAX),
  url: z
    .string()
    .trim()
    .min(1)
    .max(BANNER_ACTION_URL_MAX)
    .refine(isNotificationActionUrl, "Use a storefront path like /boxes or an https:// link."),
});
export type BannerAction = z.infer<typeof bannerActionSchema>;

/**
 * One slide, as stored in `storefront_banners.slides` and as the storefront receives it: art for
 * wide screens and for phones, alt text ("" = decorative) and an optional call to action.
 */
export const bannerSlideSchema = z.object({
  id: z.string().min(1).max(64),
  desktopImageUrl: z.url().max(BANNER_IMAGE_URL_MAX),
  mobileImageUrl: z.url().max(BANNER_IMAGE_URL_MAX),
  altText: z.string().trim().max(BANNER_ALT_TEXT_MAX),
  action: bannerActionSchema.nullable(),
});
export type BannerSlide = z.infer<typeof bannerSlideSchema>;

/** A slide as the dashboard saves it: a new one has no id yet, and the server mints one. */
export const bannerSlideInputSchema = bannerSlideSchema.extend({
  id: z.string().min(1).max(64).optional(),
});
export type BannerSlideInput = z.infer<typeof bannerSlideInputSchema>;

/**
 * A banner as the dashboard saves it, whole. `startsAt` null = live as soon as it is enabled;
 * `endsAt` null = until it is paused or deleted. `spendBands` empty = everyone.
 */
export const bannerInputSchema = z
  .object({
    name: z.string().trim().min(1).max(BANNER_NAME_MAX),
    enabled: z.boolean(),
    startsAt: z.iso.datetime({ offset: true }).nullable(),
    endsAt: z.iso.datetime({ offset: true }).nullable(),
    spendBands: z.array(bannerSpendBandSchema).max(BANNER_SPEND_BANDS.length),
    slides: z.array(bannerSlideInputSchema).min(1).max(BANNER_MAX_SLIDES),
  })
  .refine(
    (banner) =>
      banner.startsAt === null ||
      banner.endsAt === null ||
      Date.parse(banner.endsAt) > Date.parse(banner.startsAt),
    { path: ["endsAt"], message: "The end must be after the start." },
  );
export type BannerInput = z.infer<typeof bannerInputSchema>;

/** Where a banner stands at `now`: switched off, not started, running, or over. */
export type BannerState = "paused" | "scheduled" | "live" | "ended";

export function bannerState(
  banner: { enabled: boolean; startsAt: Date | string | null; endsAt: Date | string | null },
  now: Date,
): BannerState {
  if (!banner.enabled) return "paused";
  const at = now.getTime();
  if (banner.endsAt !== null && new Date(banner.endsAt).getTime() <= at) return "ended";
  if (banner.startsAt !== null && new Date(banner.startsAt).getTime() > at) return "scheduled";
  return "live";
}

/** A live banner as the storefront receives it: its slides, in order, and nothing staff-facing. */
export const storefrontBannerSchema = z.object({
  id: z.string(),
  slides: z.array(bannerSlideSchema),
});
export type StorefrontBanner = z.infer<typeof storefrontBannerSchema>;

export const storefrontBannersResponseSchema = z.object({
  banners: z.array(storefrontBannerSchema),
});
export type StorefrontBannersResponse = z.infer<typeof storefrontBannersResponseSchema>;

/**
 * What the BFF sends `POST /v1/storefront/banners`: the store's host, and the shopper's session
 * when there is one. No session, or one that no longer verifies, reads as signed out.
 */
export const storefrontBannersRequestSchema = z.object({
  host: z.string().min(1).max(255),
  sessionToken: z.string().min(1).max(256).optional(),
});
